<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="h-auto overflow-auto flex flex-col shadow">
      <woot-modal-header-v2
        :header-title="$t('AGENT_MGMT.ADD.TITLE')"
        :header-content="$t('AGENT_MGMT.ADD.DESC')"
        :custom-classes="'z-[60] border-b border-gray-100'"
        style="box-shadow: 0px 1px 4px #cacaca29"
      >
        <template #titleIcon>
          <span class="material-symbols-outlined text-icon-primary"> add </span>
        </template>
      </woot-modal-header-v2>

      <form class="form-container flex flex-col w-full items-start !px-6 !py-5">
        <div class="w-full">
          <label
            class="text-gray-700 mb-1.5 leading-5 font-[400]"
            :class="{ error: $v.agentName.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.NAME.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <input
            v-model.trim="agentName"
            type="text"
            class="rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
            :placeholder="$t('AGENT_MGMT.FORM.NAME.PLACEHOLDER')"
            @input="$v.agentName.$touch"
          />
        </div>
        <div class="w-full mb-6">
          <label
            class="text-gray-700 mb-1.5 leading-5 font-[400]"
            :class="{ error: $v.agentEmail.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.EMAIL.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <input
            v-model.trim="agentEmail"
            type="text"
            class="rounded-[8px] h-[44px] px-3 py-[10px] mb-1.5"
            placeholder="example@email.com"
            :class="
              $v.agentEmail.$invalid && agentEmail.length > 0
                ? 'border-[#B91C1C] hover:border-[#B91C1C] focus:border-[#B91C1C]'
                : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
            "
            @input="$v.agentEmail.$touch"
          />
          <div
            v-if="$v.agentEmail.$invalid && agentEmail.length > 0"
            class="text-[#B91C1C] text-sm"
          >
            {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.ERROR.EMAIL_FORMAT') }}
          </div>
          <div v-else class="text-sm text-gray-700">
            {{ $t('AGENT_MGMT.FORM.EMAIL.ADD_DESC') }}
          </div>
        </div>
        <hr class="border-gray-200 w-full m-0" />
        <div class="w-full mt-6">
          <label
            :class="{ error: $v.agentType.$error }"
            class="text-gray-700 block mb-1.5 leading-5 font-[400]"
          >
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <woot-multiselect
            v-model="agentTypeSelected"
            track-by="name"
            label="label"
            selected-label
            deselect-label=""
            :options="fullRoles"
            :allow-empty="false"
            :show-labels="false"
            :placeholder="$t('AGENT_MGMT.FORM.AGENT_TYPE.PLACEHOLDER')"
            class="no-margin"
          />
          <span v-if="$v.agentType.$error" class="message">
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.ERROR') }}
          </span>
          <div class="text-sm text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.DESC') }}
          </div>
        </div>
        <div v-if="agentType === 'Agent'" class="w-full mt-3">
          <label class="text-gray-700 flex flex-row items-center font-[400]">
            <input
              v-model="agentSeeAllConversations"
              type="checkbox"
              class="custom-checkbox rounded-[4px] border-gray-300 m-0 w-[18px] h-[18px] cursor-pointer rtl:ml-2 rtl:mr-0"
            />
            {{ $t('AGENT_MGMT.FORM.SEE_ALL_CONVERSATIONS.LABEL') }}
          </label>
        </div>
        <!-- QM §4.6, §5.1.2 — the per-agent cap, settable at creation time. -->
        <div v-if="isQueueManagementEnabled" class="w-full relative mt-4">
          <label
            class="font-[400] mb-1.5 text-gray-700"
            :class="{ error: $v.agentCapacityCap.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.LABEL') }}
          </label>
          <input
            v-model.trim="agentCapacityCap"
            type="number"
            min="1"
            step="1"
            class="rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
            :placeholder="$t('AGENT_MGMT.FORM.CAPACITY_CAP.PLACEHOLDER')"
            @input="$v.agentCapacityCap.$touch"
          />
          <span
            v-if="$v.agentCapacityCap.$error"
            class="text-[#B91C1C] text-sm message"
          >
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.ERROR') }}
          </span>
          <div class="text-sm text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.DESC') }}
          </div>
        </div>

        <div class="w-full relative mt-4">
          <label class="font-[400] mb-1.5 text-gray-700">
            {{ $t('AGENT_MGMT.FORM.AGENT_SKILLS.LABEL') }}
          </label>
          <custom-attribute-selector
            attribute-type="agent_attribute"
            :init-custom-attributes="selectedCustomAttributes"
            @addAttribute="addAttribute"
            @removeAttribute="removeAttribute"
          />
          <div class="text-sm font-[400] text-gray-700 mt-1.5 mb-3">
            {{ $t('AGENT_MGMT.FORM.AGENT_SKILLS.DESC') }}
          </div>
          <custom-attributes
            attribute-type="agent_attribute"
            attribute-class="conversation--attribute"
            :init-custom-attributes="selectedCustomAttributes"
            class="even"
            @changeAttributes="changeAttributes"
          />
        </div>

        <hr class="w-full m-0 my-6 p-0 border-gray-100" />

        <div class="w-full">
          <label
            class="font-[400] mb-1.5 text-gray-700"
            :class="{ error: $v.agentTwoFactorType.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.2FA.TITLE') }}
            <span v-if="isMandatory2fa" class="text-green-500">*</span>
          </label>
          <woot-multiselect
            v-model="onAgentTwoFactorTypeChange"
            label="name"
            selected-label=""
            deselect-label=""
            :options="twoFAoptions"
            :allow-empty="true"
            :show-labels="false"
            class="no-margin"
            :placeholder="
              $t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.PLACEHOLDER')
            "
          />
          <div class="text-sm font-[400] text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.2FA.DESC') }}
          </div>
        </div>

        <div v-show="agentTwoFactorType" class="mt-4 w-full">
          <div v-show="agentTwoFactorType === 'sms'">
            <div class="w-full">
              <label
                class="font-[400] mb-1.5 text-gray-700"
                :class="{ 'text-[#B91C1C]': $v.agentPhone1.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.LABEL') }}
                <span class="text-green-500">*</span>
              </label>
              <woot-phone-input
                v-model="agentPhone1"
                placeholder="XXX-XXXXXX"
                :has-error-in-phone-input="
                  $v.agentPhone1.$error ||
                  !$v.agentTwoFactorType.emptyPhone ||
                  $v.agentPhone1.$invalid
                "
                :show-flag-in-selection="true"
                :dropdown-direction="'top'"
              />
              <span v-if="$v.agentPhone1.$error" class="text-[#B91C1C] text-sm">
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.ERROR.FORMAT') }}
              </span>
              <div
                v-if="!$v.agentTwoFactorType.emptyPhone"
                class="text-[#B91C1C] text-sm"
              >
                <span class="message">
                  {{
                    $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.ERROR.EMPTY_PHONE')
                  }}
                </span>
              </div>
            </div>
          </div>
          <div v-show="agentTwoFactorType === 'email'" class="w-full">
            <div class="w-full">
              <label
                class="font-[400] mb-1.5 text-gray-700"
                :class="{ error: $v.agentEmail1.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.LABEL') }}
                <span class="text-green-500">*</span>
              </label>
              <input
                v-model="agentEmail1"
                type="email"
                placeholder="example@email.com"
                class="rounded-[8px] border h-[44px] px-3 py-[10px] mb-1.5"
                :class="
                  $v.agentEmail1.$invalid && agentEmail1.length > 0
                    ? 'border-[#B91C1C] hover:border-[#B91C1C] focus:border-[#B91C1C]'
                    : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                "
                @input="$v.agentEmail1.$touch"
              />
              <div
                v-if="!$v.agentTwoFactorType.emptyEmail"
                class="text-[#B91C1C] text-sm"
              >
                {{
                  $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.ERROR.EMPTY_EMAIL')
                }}
              </div>
              <div
                v-if="$v.agentEmail1.$invalid && agentEmail1.length > 0"
                class="text-[#B91C1C] text-sm"
              >
                {{
                  $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.ERROR.EMAIL_FORMAT')
                }}
              </div>
              <div v-else class="text-sm font-[400] text-gray-700 mt-1.5">
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.DESC') }}
              </div>
            </div>
          </div>
        </div>
      </form>
      <div
        class="flex flex-row justify-end gap-2 pt-4 pb-6 px-6 w-full z-[60] border-t border-gray-100"
        style="box-shadow: 0px -1px 4px #cacaca29"
      >
        <woot-button
          class="cancel-filter-btn button clear text-base px-5 py-3 leading-[20px] h-[48px] font-[400]"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('AGENT_MGMT.FORM.CANCEL_BUTTON_TEXT') }}
        </woot-button>
        <woot-submit-button
          :disabled="
            $v.agentEmail.$invalid ||
            $v.agentName.$invalid ||
            $v.agentTwoFactorType.$invalid ||
            $v.agentPhone1.$invalid ||
            $v.agentCapacityCap.$invalid ||
            uiFlags.isCreating
          "
          :button-text="$t('AGENT_MGMT.FORM.ADD_AGENT_SUBMIT_LABEL')"
          :loading="uiFlags.isCreating"
          class="button-filled text-base px-5 py-3 font-[400] h-[48px]"
          @click.native.prevent="addAgent"
        />
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { required, minLength, helpers } from 'vuelidate/lib/validators';
const strictEmail = helpers.regex('strictEmail', /^[^\s@]+@[^\s@]+\.[^\s@]+$/);
import { isPhoneIsraelEmpty } from 'shared/helpers/Validators';
import { mapGetters } from 'vuex';
import CustomAttributes from './CustomAttributes.vue';
import CustomAttributeSelector from './CustomAttributesSelector.vue';
// QM §4.6 — reused purely for its `isQueueManagementEnabled` computed.
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import {
  startConfettiInner,
  stopConfettiInner,
} from '../inbox/helpers/confetti';
export default {
  components: {
    CustomAttributes,
    CustomAttributeSelector,
  },
  mixins: [queueSupervisionMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      agentName: '',
      agentEmail: '',
      agentType: 'Agent',
      agentSeeAllConversations: false,
      vertical: 'bottom',
      horizontal: 'center',
      // agentTwoFactorAuth: false,
      agentTwoFactorType: '',
      agentPhone1: '',
      agentEmail1: '',
      // QM §4.6 — empty means "no limit".
      agentCapacityCap: '',
      roles: [
        {
          name: 'administrator',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
        {
          name: 'Agent',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.AGENT'),
        },
      ],
      show: true,
      selectedCustomAttributes: {},
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'agents/getUIFlags',
      getAccount: 'accounts/getAccount', // d99d
      accountId: 'getCurrentAccountId', // d99d
      rolesFromStore: 'roles/getRoles',
    }),
    // d99d
    account() {
      return this.getAccount(this.accountId);
    },
    isMandatory2fa() {
      return this.account?.mandatory_2fa;
    },
    newRulesSystemEnabled() {
      return this.account?.features?.new_rules_system;
    },
    fullRoles() {
      if (!this.newRulesSystemEnabled) {
        return this.roles;
      }
      return this.rolesFromStore.map(role => ({
        name: role.name,
        label: this.$t(`ROLES.NAMES.${role.name}`),
      }));
    },
    twoFAoptions() {
      return [
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.OFF'),
          value: '',
          $isDisabled: this.isMandatory2fa,
        },
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.sms'),
          value: 'sms',
        },
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.email'),
          value: 'email',
        },
      ];
    },
    onAgentTwoFactorTypeChange: {
      get() {
        if (!this.agentTwoFactorType) return this.twoFAoptions[0];
        return this.twoFAoptions.find(i => i.value === this.agentTwoFactorType);
      },
      set(option) {
        this.agentTwoFactorType = option.value;
      },
    },
    agentTypeSelected: {
      get() {
        const selected = this.fullRoles.find(
          opt => opt.name === this.agentType
        );
        return selected;
      },
      set(option) {
        this.agentType = option.name;
      },
    },
  },
  watch: {
    agentTwoFactorType(newVal) {
      if (newVal === 'email' && !this.agentEmail1) {
        this.agentEmail1 = this.agentEmail;
      }
    },
    agentPhone1() {
      this.$v.agentPhone1.$touch();
    },
  },
  // d99d
  mounted() {
    if (this.isMandatory2fa) {
      this.agentTwoFactorType = 'sms';
    }
    if (this.newRulesSystemEnabled) {
      this.$store.dispatch('roles/get');
    }
  },
  // .
  validations: {
    agentName: {
      required,
      minLength: minLength(1),
    },
    agentEmail: {
      required,
      email: strictEmail,
    },
    agentType: {
      required,
    },
    agentPhone1: {
      isPhoneIsraelEmpty,
    },
    agentEmail1: {
      required,
      email: strictEmail,
    },
    // QM §4.6 — empty is valid and means "no limit"; anything else must be a whole number > 0.
    agentCapacityCap: {
      positiveIntegerOrBlank(value) {
        return (
          value === '' || value === null || /^[1-9]\d*$/.test(String(value))
        );
      },
    },
    agentTwoFactorType: {
      emptyPhone: function (value) {
        return value !== 'sms' || !!this.agentPhone1;
      },
      emptyEmail: function (value) {
        return value !== 'email' || !!this.agentEmail1;
      },
    },
  },
  methods: {
    showAlert(message) {
      bus.$emit('newToastMessage', message);
    },
    async addAgent() {
      if (this.agentType === 'administrator') {
        // d99d
        this.agentSeeAllConversations = undefined;
      }
      try {
        await this.$store.dispatch('agents/create', {
          name: this.agentName,
          email: this.agentEmail,
          role: this.agentType,
          custom_attributes: this.selectedCustomAttributes,
          two_factor_type: this.agentTwoFactorType, // d99d
          phone1: this.agentPhone1, // d99d
          email1: this.agentEmail1, // d99d
          see_all_conversations: this.agentSeeAllConversations, // d99d
          capacity_cap: this.agentCapacityCap, // QM §4.6
        });
        this.showAlert(this.$t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
        startConfettiInner();
        setTimeout(() => {
          stopConfettiInner();
        }, 10000);
      } catch (error) {
        const { response: { data: { error: errorResponse = '' } = {} } = {} } =
          error;
        let errorMessage = '';
        if (error.response.status === 422) {
          errorMessage = this.$t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
        } else {
          errorMessage = this.$t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
        }
        this.showAlert(errorResponse || errorMessage);
      }
    },
    addAttribute(attributeKey, attributeValue) {
      this.$set(this.selectedCustomAttributes, attributeKey, attributeValue);
    },
    removeAttribute(attributeKey) {
      const { [attributeKey]: _, ...rest } = this.selectedCustomAttributes;
      this.selectedCustomAttributes = rest;
    },
    changeAttributes(customAttrs) {
      this.selectedCustomAttributes = customAttrs;
    },
  },
};
</script>
<style lang="scss" scoped>
.custom-checkbox {
  appearance: none;
  @apply border border-gray-300 relative bg-white;

  &:checked {
    @apply bg-green-500 border-green-500;

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 5px;
      height: 9px;
      border: 1px solid #fff;
      border-top: none;
      border-left: none;
      transform: rotate(45deg);
    }
  }
}

.footer-text {
  white-space: pre-line;
}

.form-container {
  max-height: 500px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-[#c1c1c1] rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
<style lang="scss">
.add-agent-modal {
  .modal-container {
    @apply rounded-[12px];
  }
}
</style>
